import { useBrowserPushNotifications } from "@app/account/hooks/use-browser-push-notifications.hook";
import { useFeatureFlag } from "@app/shared/hooks/use-feature-flag";
import { safeSessionStorage } from "@core/shared/utils/session-storage";
import { userAtom } from "@framework/config/atoms";
import { trans, plainTrans } from "@mongez/localization";
import { BellRing, ShieldCheck, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

export function BrowserNotificationsPrompt() {
  const user = userAtom.useValue();
  const notificationsEnabled = useFeatureFlag("notifications");
  const [dismissed, setDismissed] = useState(true);
  const promptKey = useMemo(
    () => `browser-notifications-prompt:${user.id || "student"}`,
    [user.id],
  );
  const pushNotifications = useBrowserPushNotifications(
    Boolean(user.isStudent && notificationsEnabled),
    () => setDismissed(true),
  );

  useEffect(() => {
    if (!user.isStudent || !notificationsEnabled) return;

    setDismissed(safeSessionStorage.getItem(promptKey) === "dismissed");
  }, [notificationsEnabled, promptKey, user.isStudent]);

  const close = () => {
    safeSessionStorage.setItem(promptKey, "dismissed");
    setDismissed(true);
  };

  if (
    dismissed ||
    pushNotifications.isLoading ||
    !pushNotifications.isSupported ||
    !pushNotifications.isConfigured ||
    pushNotifications.isSubscribed ||
    pushNotifications.permission === "denied"
  ) {
    return null;
  }

  return (
    <aside className="fixed bottom-5 left-4 right-4 z-[1200] mx-auto max-w-xl overflow-hidden rounded-[1.5rem] border border-violet-200 bg-white shadow-2xl shadow-violet-950/20 dark:border-violet-900 dark:bg-slate-950 sm:left-auto sm:right-6 sm:w-[30rem] rtl:sm:left-6 rtl:sm:right-auto">
      <div className="relative p-5 sm:p-6">
        <button
          type="button"
          onClick={close}
          className="absolute left-4 top-4 rounded-full p-2 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-slate-800 dark:hover:text-white rtl:left-auto rtl:right-4"
          aria-label={plainTrans("close")}
        >
          <X size={18} />
        </button>

        <div className="flex items-start gap-4 pe-9">
          <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-violet-600 to-fuchsia-500 text-white shadow-lg shadow-violet-500/25">
            <BellRing size={27} />
          </div>
          <div>
            <h2 className="text-lg font-black text-slate-950 dark:text-white">
              {trans("notifications.permissionPromptTitle")}
            </h2>
            <p className="mt-1 text-sm leading-6 text-slate-600 dark:text-slate-300">
              {trans("notifications.permissionPromptDescription")}
            </p>
          </div>
        </div>

        <div className="mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:items-center sm:justify-end">
          <button
            type="button"
            onClick={close}
            className="rounded-xl px-4 py-2.5 text-sm font-bold text-slate-500 transition hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800"
          >
            {trans("notifications.permissionPromptLater")}
          </button>
          <button
            type="button"
            disabled={pushNotifications.isUpdating}
            onClick={() => pushNotifications.subscribe()}
            className="inline-flex items-center justify-center gap-2 rounded-xl bg-slate-950 px-5 py-2.5 text-sm font-black text-white transition hover:bg-violet-700 disabled:cursor-wait disabled:opacity-60 dark:bg-violet-600 dark:hover:bg-violet-500"
          >
            <ShieldCheck size={18} />
            {trans("notifications.browserPushEnable")}
          </button>
        </div>
      </div>
    </aside>
  );
}
